import "./TableWrapper.scss";
import TableHead from "./TableHead";
import TableBody from "./TableBody";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { useEffect, useState } from "react";
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import TanstackPagination from "./Pagination";
import React from "react";
import ComponentTabs from "../../../Tabs/ComponentTabs/ComponentTabs";
import { useDispatch, useSelector } from "react-redux";
import {
  setActiveTab,
  setPaginationPageNumber,
} from "../../../../../slices/common/reducer";
import { apiParams, sortApiParams } from "../../../../../Utils/CommonUtils";
import NoRecords from "./NoRecords";
import useRezReactTable from "../hooks/useRezReactTable";
import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";
// import LoadingSpinner from "../../../LoadingSpinner/LoadingSpinner";

function TableWrapper({
  featureOptions,
  columns,
  data,
  tabData,
  apiTabData,
  hideTabs,
  hideTabsWrapper,
  hideTabsButtons,
  hidePagination,
  activeTabName,
  activeTabState,
  activeTabReducer,
  hideCustomiseColumn,
  filterToggle,
  filterShow,
  paginatedApi,
  filterApi,
  filterApiEntity,
  filterApiListEntity,
  filterApiFunction,
  filterApiService,
  filterForm,
  statusColumn,
  id,
  totalRows,
  status,
  search,
  serverSort,
  serverSortColumn,
  serverSortOrder,
  download,
  handleDownload,
  parentId,
  organizationId,
  ownerId,
  enterpriseId,
  userId,
  organizationType,
  organizationIdUser,
  parentId2,
  parentType,
  orgType,
  agId,
  statusCheck,
}) {
  const dispatch = useDispatch();
  const triggerRender = useSelector((state) => state.common.triggerRender);
  const triggerFilterButtonApi = useSelector(
    (state) => state.common.trigger.filterButton
  );

  const activeTab = useSelector((state) => state.common.activeTab);
  const loading = useSelector((state) => state.common.pagination.loading);
  const metadataLoading = useSelector((state) => state.common.loader.metaData);
  // const [loading, setLoading] = useState(false);

  
  const tableOptions = useRezReactTable();

  const {
    sorting,
    columnVisibility,
    globalFilter,
    grouping,
    pagination,
    setColumnVisibility,
    setSorting,
    setGlobalFilter,
    setGrouping,
    setPagination,
  } = tableOptions;

  useEffect(() => {
    dispatch(setPaginationPageNumber(0));

    let sortColumn = serverSortColumn;
    let sortOrder = serverSortOrder;

    if (sorting.length !== 0) {
      sortColumn = sorting[0]?.id;
      sortOrder = sorting[0]?.desc ? "DESC" : "ASC";
    }

    const isCheckboxColumn = sortColumn === "checkbox";
     
    if (paginatedApi || filterApi) {
      if (filterApi || serverSort) {
        const params = sortApiParams(
          serverSort,
          pagination.pageIndex,
          pagination.pageSize,
          isCheckboxColumn ? undefined : sortColumn, // If checkbox, don't send sortColumn
          isCheckboxColumn ? undefined : sortOrder,
          statusColumn,
          activeTabState ? activeTabState : activeTab,
          filterApiListEntity
        );
        return filterApiService(
          filterApiEntity,
          filterApiFunction,
          filterForm,
          params,
          activeTab,
          parentId,
          organizationId,
          ownerId,
          enterpriseId,
          userId,
          organizationType,
          organizationIdUser,
          parentId2,
          parentType,
          orgType,
          agId,
          statusCheck
        );
      }
      paginatedApi(
        apiParams(
          pagination.pageIndex,
          pagination.pageSize,
          activeTab,
          statusColumn,
          id
        ),
        status
      );
    }
  }, [
    triggerRender,
    triggerFilterButtonApi,
    activeTab,
    pagination,
    sorting,
    filterForm,
  ]);

  useEffect(() => {
    dispatch(setPaginationPageNumber(0));
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  }, [triggerRender, triggerFilterButtonApi, activeTab]);

  const handleTabChange = (newTab) => {
    dispatch(setActiveTab(newTab));
    dispatch(setPaginationPageNumber(0));
  };

  const featureOptionsDefault = {
    enableColumnResizing: true,
    enableSorting: true,
    enableFilter: true,
    enableTopbar: true,
    enableGlobalFilter: true,
    enableSearch: true,
    enableColumnPadding: true,
    enableColumnReording: true,
    enableColumnHiding: true,
    enableVerticalDots: false,
    enableColumnGrouping: false,
    enableColumnPinning: true,
    enableServerSidePagination: true,
    // Add other conditions above featureOptions
    ...featureOptions,
  };

  const {
    enableFilter,
    enableGlobalFilter,
    enableColumnHiding,
    enableSorting,
    enableColumnResizing,
    enableColumnPinning,
    enableServerSidePagination,
    enableColumnReording,
  } = featureOptionsDefault;

  const [columnOrder, setColumnOrder] = useState(() =>
    columns?.map((c) => c.accessorKey)
  );

  useEffect(() => {
    setColumnOrder(columns?.map((c) => c.accessorKey));
  }, [columns]);

  const table = useReactTable({
    columns,
    data,
    state: {
      columnOrder,
      sorting,
      columnVisibility,
      globalFilter,
      grouping,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onGroupingChange: setGrouping,
    enableColumnFilters: enableFilter,
    enableGlobalFilter: enableGlobalFilter,
    enableHiding: enableColumnHiding,
    enableSorting: enableSorting,
    manualSorting: serverSort ? true : undefined,
    enableGrouping: false,
    enableColumnResizing: enableColumnResizing,
    enableColumnPinning: enableColumnPinning,
    columnResizeDirection: "ltr",
    columnResizeMode: "onChange",
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    onPaginationChange: setPagination,
    manualPagination: enableServerSidePagination,
    pageCount: Math.floor(totalRows / pagination.pageSize) + 1,
  });

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id);
        const newIndex = columnOrder.indexOf(over.id);
        return arrayMove(columnOrder, oldIndex, newIndex); //this is just a splice util
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );


  return (
    <div className="ts_table_tabs_wrapper">
      <ComponentTabs
        activeTab={activeTabState ? activeTabState : activeTab}
        activeTabName={activeTabName ? activeTabName : "ALL"}
        setActiveTab={activeTabReducer ? activeTabReducer : setActiveTab}
        data={data}
        tabData={tabData}
        apiTabData={apiTabData}
        hideCustomiseColumn={hideCustomiseColumn}
        filterToggle={filterToggle}
        filterShow={filterShow}
        onTabChange={handleTabChange}
        search={search}
        hideTabs={hideTabs}
        hideTabsWrapper={hideTabsWrapper}
        hideTabsButtons={hideTabsButtons}
        table={table}
        tableOptions={tableOptions}
        featuresOptions={featureOptionsDefault}
        download={download}
        handleDownload={handleDownload}
      />
      {/* {loading ? (
        <LoadingSpinner
          title={"Loading data..."}
          className={"table-loader-wrapper"}
        />
      ) : (
        <div className="ts_table_wrapper">
          {table.options.data.length === 0 ? (
            <NoRecords />
          ) : (
            <>
              <div className={"ts__table__wrapper"}>
                {enableColumnReording ? (
                  <DndContext
                    collisionDetection={closestCenter}
                    modifiers={[restrictToHorizontalAxis]}
                    onDragEnd={handleDragEnd}
                    sensors={sensors}
                  >
                    <table className="ts__table">
                      <TableHead
                        table={table}
                        featureOptions={featureOptionsDefault}
                        tableOptions={tableOptions}
                        columnOrder={columnOrder}
                      />
                      <TableBody
                        table={table}
                        tableOptions={tableOptions}
                        columnOrder={columnOrder}
                        featureOptions={featureOptionsDefault}
                      />
                    </table>
                  </DndContext>
                ) : (
                  <table className="ts__table">
                    <TableHead
                      table={table}
                      featureOptions={featureOptionsDefault}
                      tableOptions={tableOptions}
                      columnOrder={columnOrder}
                    />
                    <TableBody
                      table={table}
                      tableOptions={tableOptions}
                      columnOrder={columnOrder}
                      featureOptions={featureOptionsDefault}
                    />
                  </table>
                )}
              </div>

              {!hidePagination && (
                <TanstackPagination table={table} totalRows={totalRows} />
              )}
            </>
          )}
        </div> */}
      {loading ? (
        <LoadingSpinner
          title="Loading data..."
          className="table-loader-wrapper"
        />
      ) : (
        // Check if data exists
        <div className="ts_table_wrapper">
          {data && data.length > 0 ? (
            <div className="ts__table__wrapper">
              {enableColumnReording ? (
                <DndContext
                  collisionDetection={closestCenter}
                  modifiers={[restrictToHorizontalAxis]}
                  onDragEnd={handleDragEnd}
                  sensors={sensors}
                >
                  <table className="ts__table">
                    <TableHead
                      table={table}
                      featureOptions={featureOptionsDefault}
                      tableOptions={tableOptions}
                      columnOrder={columnOrder}
                    />
                    <TableBody
                      table={table}
                      tableOptions={tableOptions}
                      columnOrder={columnOrder}
                      featureOptions={featureOptionsDefault}
                    />
                  </table>
                </DndContext>
              ) : (
                <table className="ts__table">
                  <TableHead
                    table={table}
                    featureOptions={featureOptionsDefault}
                    tableOptions={tableOptions}
                    columnOrder={columnOrder}
                  />
                  <TableBody
                    table={table}
                    tableOptions={tableOptions}
                    columnOrder={columnOrder}
                    featureOptions={featureOptionsDefault}
                  />
                </table>
              )}
            </div>
          ) : (
            <NoRecords />
          )}
          {!hidePagination && (
            <TanstackPagination table={table} totalRows={totalRows} />
          )}
        </div>
      )}
    </div>
  );
}

export default TableWrapper;
